import React, { ComponentProps, useContext, useState, useEffect } from 'react';
import { asWidgetProps } from 'Src/types/widget-helpers';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useAppContext } from 'Context/ReactAppContext';
import { FilterWhen } from 'Components/widgets/criteria/criteria-when/filter-when/FilterWhen';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import { CriteriaModal } from 'Components/widgets/criteria-modal/CriteriaModal';
import { t } from 'Src/i18n';
import { changeTripDuration } from 'Store/faceting/facetingSearch.action';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { DEFAULT_TRIP_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { getDepartureDate } from 'Store/trip-criteria/tripCriteria.selector';
type CriteriaModalWhenProps = {
  durationOptions: Array<number>;
  onClose: () => void;
};
export const CriteriaModalWhen = ({ durationOptions, onClose }: CriteriaModalWhenProps) => {
  const [criteriaContext, setCriteriaContext] = useContext(CriteriaContext);
  const appContext = useAppContext();
  const departureDate: moment$Moment | null | undefined = useSelector(getDepartureDate);
  const [inputDepartureDate, setInputDepartureDate] = useState(departureDate);
  const [inputDuration, setInputDuration] = useState(0);
  const [validated, setValidated] = useState(false);
  const dispatch = useDispatch();
  const track = useTracking();
  const persistDepartureDate = () => {
    const invalid = !(
      departureDate ||
      moment.isMoment(inputDepartureDate) ||
      moment(inputDepartureDate).isValid()
    );
    if (invalid) {
      return dispatch(setDepartureDate(null));
    }
    dispatch(setDepartureDate(moment(inputDepartureDate).valueOf()));
    setCriteriaContext({
      defaultValueForCalendar: departureDate,
    });
    if (criteriaContext.trackingActionName) {
      track({
        actionGA: criteriaContext.trackingActionName,
        labelGA: `when_${moment(inputDepartureDate).format('DD/MM/YYYY')}`,
      });
    }
  };
  const persistDuration = () => {
    let durationToPersist = inputDuration;
    if (!inputDuration && inputDepartureDate) {
      durationToPersist = DEFAULT_TRIP_DURATION;
    }
    if (durationToPersist) {
      dispatch(changeTripDuration(durationToPersist));
      if (criteriaContext.trackingActionName) {
        const labelGA = `duration_${durationToPersist}`;
        track({
          actionGA: criteriaContext.trackingActionName,
          labelGA,
        });
      }
    }
  };
  useEffect(() => {
    if (validated) {
      onClose();
    }
  }, [validated]);
  const offsetCalendar = appContext.isSeller ? 0 : undefined;
  return (
    <CriteriaModal
      {...asWidgetProps<ComponentProps<typeof CriteriaModal>>({
        onValidate: () => {
          if (inputDepartureDate && inputDuration) {
            persistDepartureDate();
            persistDuration();
            setValidated(true);
          }
        },
        onClose: onClose,
        onReset: () => {
          setInputDuration(0);
          setInputDepartureDate(null);
        },
      })}
    >
      <FilterWhen
        {...asWidgetProps<ComponentProps<typeof FilterWhen>>({
          optionsDuration: durationOptions,
          changeTripDuration: (time) => {
            setInputDuration(time);
          },
          changeDepartureDate: (value) => {
            setInputDepartureDate(value ? moment(value, moment.defaultFormat) : null);
          },
          clearDepartureDate: () => {
            setInputDepartureDate(null);
          },
          clearTripDuration: () => {
            setInputDuration(0);
          },
          duration: inputDuration,
          departureDate: inputDepartureDate,
          openCalendarToDate: criteriaContext.defaultValueForCalendar,
          dateFormat: t('booking.form.dateFormat'),
          label: t('faceting.compare.warningMessage'),
          isSubmittable: true,
          offsetCalendar: offsetCalendar,
          theme: 'compact',
        })}
      />
    </CriteriaModal>
  );
};
